<template>
  <div>
    <v-card class="my-1">
      <v-card-title>EA Form</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-autocomplete
            v-model="selectedYear"
            v-validate="'required'"
            label="Year"
            :items="years"
            clearable
            hide-selected
            :error-messages="errors.collect('selectedYear')"
            data-vv-name="selectedYear"  
            data-vv-as="year"
          />
          <v-autocomplete
            v-model="selectedGroups"
            :items="groupOptions"
            label="Company Groups"
            item-text="text"
            item-value="value"
            multiple
            chips
            small-chips
            deletable-chips
            hide-selected
            clearable
          />
          <v-autocomplete
            v-model="selectedCompanies"
            v-validate="'required'"
            label="Companies"
            item-text="name"
            item-value="id"
            :items="companyOptions"
            clearable
            multiple
            hide-selected 
            chips
            small-chips
            deletable-chips
            :error-messages="errors.collect('selectedCompanies')"
            data-vv-name="selectedCompanies"  
            data-vv-as="companies"
          />
        </v-col>
  
        <v-col
          cols="12"
          class="ma-auto"
        >      
          <v-row class="text-center">
            <v-col cols="12">
              <payroll-report-ea-form-release
                class="mb-2"
                :loading="loading"
                :selected-year="selectedYear"
                :selected-companies="selectedCompanies"
                :employees="employees"
                :companies="companies"
              />
              <payroll-report-ea-form-generate
                class="mb-2"
                :loading="loading"
                :selected-year="selectedYear"
                :selected-companies="selectedCompanies"
                :employees="employees"
                :companies="companies"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  export default {
    components: {
      PayrollReportEaFormRelease: () => import('@/components/hris/payroll/report/income-tax-report/EaFormRelease'),
      PayrollReportEaFormGenerate: () => import('@/components/hris/payroll/report/income-tax-report/EaFormGenerate'),
    },
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      years: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        selectedGroups: [],
        selectedCompanies: [],
        selectedYear: 0,
        employees: [],
      }
    },
    computed: {
      computedFilter () {
        if (this.selectedCompanies.length < 1 || !this.selectedYear) return
        return `${this.selectedCompanies}|${this.selectedYear}`
      },
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },
    },
    watch: {
      computedFilter: {
        immediate: true,
        handler (newVal) {
          if (newVal) {
            const [newCompanies, newSelecteYear] = newVal.split('|')       
            if (newCompanies !== null && newSelecteYear !== null) {
              this.fetchEaEmployees()
            }
          }
        },
      },
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    methods: {
      prepareFormData () {
        return {
          year: this.selectedYear,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies }
      },
      fetchEaEmployees () {
        this.employees = []
                
        this.$api.payrollReports().listEaEmployees(this.prepareFormData())
          .then(res => {
            if (res.data.length > 0) {
              res.data.map(data => {
                this.employeeObj = {
                  company_employee_uid: data.companyEmployee.company_employee_uid,
                  company_uid: data.companyEmployee.company.id,
                  name: data.companyEmployee.employee.person.name,
                  company_name: data.companyEmployee.company.name,
                  start_date: data.companyEmployee.start_date,
                  end_date: data.companyEmployee.end_date,
                }
                this.employees.push(this.employeeObj)

                this.employees = _.orderBy(this.employees, ['name', 'start_date'], ['asc', 'desc'])

                this.employees = this.employees.filter((e, i) => {
                  return this.employees.findIndex((x) => {
                    return x.name === e.name && x.company_uid === e.company_uid && (x.end_date && e.end_date === this.$moment(x.start_date).add(1, 'days') ? e.end_date === this.$moment(x.start_date).add(1, 'days') : x.end_date ? x.end_date : x.end_date === null)
                  }) === i
                })
              })
            } else {
              this.$snack({ 
                color: 'error',
                message: 'No Records Found', 
              })              
            }
          })    
      },
    },
  }
</script>
